import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { RRule, RRuleSet, rrulestr } from 'rrule';
import { IIntervalResponse } from '@nimbus-global/features/opening-hours/models/i-interval.response';
import { IntervalMapper } from '@nimbus-global/features/opening-hours/services/interval.mapper';

@Injectable({ providedIn: 'root' })
export class RecurrentEventsService {
  private readonly numberofDays = 7;
  private readonly minutesInDay = 1440;
  private readonly minutesInHour = 60;

  private readonly yearLimitForRecurrentEvents = 2;
  public constructor(private readonly intervalMapper: IntervalMapper) {}

  public getEventsFromRecurrentRule(
    recurrenceRule: string,
    recurrenceExceptions: moment.Moment[],
    start: Date,
    end?: Date,
    tzid?: string,
  ): Date[] {
    // TODO: Remove this workaround when the bug in rrulestr will be fixed or replace rrulestr with other library
    const r = rrulestr(recurrenceRule, {
      dtstart: start, // Do not send tzid to rrulestr because it may generate invalid dates for certain timezones https://github.com/jakubroztocil/rrule/issues/453
    });

    const duration = moment.duration(moment(start).diff(moment(start).startOf('day')));

    duration.add(start.getTimezoneOffset(), 'minutes');

    // Rule adjustment is necessary because of the bug in rrulestr. It doesn't take into account the timezone offset.
    r.options.byhour = [duration.get('hours')];
    r.options.byminute = [duration.get('minutes')];

    const exceptions = recurrenceExceptions.map((d) => moment(d));

    const cap = end || r.options.until || moment().add(this.yearLimitForRecurrentEvents, 'year').toDate();

    const recurrenceEventEnd = end || r.options.until;

    const eventOffsetAtStart = moment(start).tz(tzid).utcOffset();

    const eventTimestamp = moment
      .duration(start.getUTCHours(), 'hours')
      .add(start.getUTCMinutes(), 'minutes')
      .add(eventOffsetAtStart, 'minutes');

    this.adjustRecurrentRule(r, eventOffsetAtStart, start);

    let allDate = r.options.until ? r.all() : r.between(start, cap, true);

    // In RRULE startDate TimezoneOffset is set but if recurrenceRule has a date range
    // where summertime will be change into winter time and vice versa we need to handle
    // TimezoneOffset for 2 periods (summer and winter time)
    if (allDate.some((date) => date.getTimezoneOffset() !== start.getTimezoneOffset())) {
      allDate = allDate.map((date) => {
        const currentDate = moment(date);
        const startDate = moment(start);
        const diff = moment.duration({ hours: 1 });

        if (!startDate.isDST() && currentDate.isDST()) {
          return currentDate.subtract(diff).toDate();
        }

        if (startDate.isDST() && !currentDate.isDST()) {
          return currentDate.add(diff).toDate();
        }

        return date;
      });
    }

    const result = allDate

      .map((d) => {
        if (tzid === moment.tz.guess()) {
          const normalizedDate = moment(d).set({
            hour: start.getHours(),
            minute: start.getMinutes(),
          });

          return normalizedDate.toDate();
        }

        const normalizedDate = moment(d).set({
          hour: eventTimestamp.get('hours'),
          minute: eventTimestamp.get('minutes'),
        });
        const localOffset = moment(d).utcOffset();
        const eventOffsetAtDate = moment(d).tz(tzid).utcOffset();

        return normalizedDate.subtract(eventOffsetAtDate, 'minutes').add(localOffset, 'minutes').toDate();
      })
      .filter(
        // Filter out the dates that are not in the range of the start and end date; that has to be done after the offset adjustment;
        (date) =>
          !exceptions.some((e) => e.isSame(date, 'day')) &&
          moment(date).isSameOrAfter(start) &&
          (!recurrenceEventEnd || moment(date).isBefore(end || r.options.until)),
      ); // Make a difference between the offset of the start and the offset of the date to always use the start daylight saving time;

    return result;
  }

  public isAliveByRecurrentRule(
    statuses: IIntervalResponse[],
    recurrentId: string,
    recurrenceRule: string,
    recurrenceExceptions: moment.Moment[],
    start: Date,
    end?: Date,
    tzid?: string,
  ): boolean {
    const events = this.getEventsFromRecurrentRule(recurrenceRule, recurrenceExceptions, start, end, tzid);

    const child = statuses.find((s) => !!s.recurrenceId && s.recurrenceId === recurrentId && !s.recurrenceRule);

    return !!child || !events || !!events.length;
  }

  public getRecurrentEventByChild(
    childId: string,
    statuses: IIntervalResponse[],
  ): { isAlive: boolean; recurrentEvent?: IIntervalResponse } {
    const child = statuses.find((status) => status.id === childId && status.recurrenceId && !status.recurrenceRule);

    if (!child) {
      return null;
    }

    const parent = statuses.find((status) => status.id === child.recurrenceId && !!status.recurrenceRule);

    if (!parent) {
      return null;
    }

    return {
      isAlive: this.isAliveByRecurrentRule(
        statuses.filter((s) => s.id !== childId),
        parent.id,
        parent.recurrenceRule,
        this.intervalMapper.getExceptionList(parent.exceptionDates, parent.timeZone),
        moment(parent.start).toDate(),
        null,
        parent.timeZone,
      ),
      recurrentEvent: parent,
    };
  }

  public getRecurrentDays(recurrenceRule: string): string[] {
    if (!recurrenceRule) {
      return [];
    }
    const recurrenceParts = recurrenceRule.substring(recurrenceRule.indexOf(':') + 1).split(';');

    const byDay = recurrenceParts.find((r) => r.startsWith('BYDAY'));

    if (!byDay) {
      return [];
    }

    return byDay.substring(byDay.indexOf('=') + 1).split(',');
  }

  private adjustRecurrentRule(rule: RRule | RRuleSet, eventOffsetAtStart: number, start: Date): void {
    if (rule?.options?.byweekday?.length) {
      const utcMinutes = start.getUTCMinutes() + start.getUTCHours() * this.minutesInHour;

      const utcMinutesWithOffset = utcMinutes + eventOffsetAtStart;
      if (utcMinutesWithOffset < 0) {
        rule.options.byweekday = rule.options.byweekday.map((day) => Math.abs((day + 1) % this.numberofDays));
      }
    }
  }
}
